/**
 * Benefits grid - Figma `6377:88008` (the Careers page).
 *
 * A four-column mosaic of square tiles. The heading and its call to action take the
 * first three columns of the top row; everything after that is a tile.
 *
 * A tile is one of two things, decided by the content rather than by a flag:
 *
 * - **a photograph**, when the card has an image;
 * - **a blue panel**, otherwise - title at the top, an optional button, and a running
 *   number in the bottom right.
 *
 * The numbers are positional and generated here. They count blue panels only, as in the
 * design: photographs are not numbered. Storing them as content would let an editor
 * produce "01, 02, 04".
 *
 * The tile button uses the standard Button, which is 45px tall. The mock-up draws it
 * smaller, but the component set defines exactly one button size - eyeballing a second
 * one here is how a design system starts to rot. Worth raising with the designer.
 */
import { CtaLink, Eyebrow, SectionShell } from '../primitives';
import { Button } from '../../components/ui/Button';
import type { BenefitsGridSection, Card } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';

function PhotoTile({ card }: { card: Card }) {
  if (!card.image) return null;
  return (
    <figure className="m-0 aspect-square overflow-hidden rounded-s bg-surface-sunken">
      <Image
        media={card.image}
        alt={card.image.alternativeText ?? card.title ?? ''}
        sizes={SIZES.third}
        className="h-full w-full object-cover"
      />
    </figure>
  );
}

function PanelTile({ card, index }: { card: Card; index: number }) {
  return (
    <div className="flex aspect-square flex-col justify-between rounded-s bg-blue-50 p-5">
      <h3 className="text-body-md text-brand-700">{card.title}</h3>

      <div className="flex items-end justify-between gap-4">
        {card.url ? <Button to={card.url}>{card.body || card.title}</Button> : <span />}
        <span className="text-body-sm text-brand-700">{String(index).padStart(2, '0')}</span>
      </div>
    </div>
  );
}

export default function BenefitsGrid({ data }: { data: BenefitsGridSection }) {
  const cards = data.cards ?? [];
  let panelNumber = 0;

  return (
    <SectionShell settings={data.settings} name="benefits-grid">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col justify-between gap-8 sm:col-span-2 lg:col-span-3">
          {data.heading ? <h2 className="max-w-4xl text-h3 text-ink lg:text-h2">{data.heading}</h2> : null}
          {data.cta ? <CtaLink value={data.cta} /> : null}
        </div>

        {cards.map((card, i) => {
          if (card.image) return <PhotoTile key={i} card={card} />;
          panelNumber += 1;
          return <PanelTile key={i} card={card} index={panelNumber} />;
        })}
      </div>
    </SectionShell>
  );
}
